import { Timestamp } from 'firebase/firestore';

export type TemplatePageSize = 'letter';

export type TemplateTextAlign = 'left' | 'center' | 'right';

export type TemplateFontSize = 'sm' | 'md' | 'lg';

export type TemplatePlaceholderFillMode = 'auto' | 'manual';

export interface TemplateTextStyle {
  fontSize: TemplateFontSize;
  align: TemplateTextAlign;
  bold: boolean;
}

export interface TemplateTextBlock {
  id: string;
  type: 'text';
  content: string;
  style: TemplateTextStyle;
}

export interface TemplatePlaceholderBlock {
  id: string;
  type: 'placeholder';
  key: string;
  label: string;
  style: TemplateTextStyle;
}

export interface TemplateSpacerBlock {
  id: string;
  type: 'spacer';
  heightMm: number;
}

export interface TemplateDividerBlock {
  id: string;
  type: 'divider';
}

export interface TemplateSignatureBlock {
  id: string;
  type: 'signature';
  label: string;
}

export type TemplateListStyle = 'cards' | 'numbered' | 'bullets';

/** Repeats one entry per item of a list variable (e.g. medicamentos recetados). */
export interface TemplateListBlock {
  id: string;
  type: 'list';
  key: string;
  label: string;
  listStyle: TemplateListStyle;
}

export type TemplateBlock =
  | TemplateTextBlock
  | TemplatePlaceholderBlock
  | TemplateSpacerBlock
  | TemplateDividerBlock
  | TemplateSignatureBlock
  | TemplateListBlock;

export type TemplateFieldType = 'text' | 'list';

/** Custom fill-at-generation fields defined on a template. */
export interface TemplateCustomField {
  key: string;
  label: string;
  /** Missing on fields saved before lists existed; treated as text. */
  type?: TemplateFieldType;
}

export type TemplateKind = 'receta';

/** Document at businesses/{businessId}/documentTemplates/{templateId}. */
export interface DocumentTemplate {
  id: string;
  name: string;
  /** Set on the built-in receta plantilla, which cannot be deleted. */
  kind?: TemplateKind;
  pageSize: TemplatePageSize;
  /** When false, PDF omits logo/consultorio header (pre-printed letterhead). */
  includeBusinessHeader: boolean;
  blocks: TemplateBlock[];
  customFields: TemplateCustomField[];
  createdBy: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface TemplatePlaceholderDef {
  key: string;
  label: string;
  fillMode: TemplatePlaceholderFillMode;
}

/** Auto-filled from patient / consultorio / fecha. */
export const TEMPLATE_AUTO_PLACEHOLDERS: TemplatePlaceholderDef[] = [
  { key: 'paciente.nombre', label: 'Nombre del paciente', fillMode: 'auto' },
  { key: 'paciente.dob', label: 'Fecha de nacimiento', fillMode: 'auto' },
  { key: 'paciente.edad', label: 'Edad del paciente', fillMode: 'auto' },
  { key: 'paciente.address', label: 'Dirección', fillMode: 'auto' },
  { key: 'paciente.phone', label: 'Teléfono', fillMode: 'auto' },
  { key: 'doctor.label', label: 'Doctor', fillMode: 'auto' },
  { key: 'consultorio.nombre', label: 'Consultorio', fillMode: 'auto' },
  { key: 'fecha', label: 'Fecha', fillMode: 'auto' },
];

/** Filled by the doctor when generating the document. */
export const TEMPLATE_MANUAL_PLACEHOLDERS: TemplatePlaceholderDef[] = [
  { key: 'libre.motivo', label: 'Motivo / detalle', fillMode: 'manual' },
  { key: 'libre.diagnostico', label: 'Diagnóstico', fillMode: 'manual' },
  { key: 'libre.observaciones', label: 'Observaciones', fillMode: 'manual' },
  { key: 'libre.recomendaciones', label: 'Recomendaciones', fillMode: 'manual' },
];

/** Full catalog for the plantillas builder. */
export const TEMPLATE_PLACEHOLDERS: TemplatePlaceholderDef[] = [
  ...TEMPLATE_AUTO_PLACEHOLDERS,
  ...TEMPLATE_MANUAL_PLACEHOLDERS,
];

export const RECETA_TEMPLATE_ID = 'receta';
export const RECETA_MEDICATIONS_KEY = 'receta.medicamentos';

/** List variables filled automatically (from the consulta when available). */
export const TEMPLATE_AUTO_LIST_PLACEHOLDERS: TemplatePlaceholderDef[] = [
  { key: RECETA_MEDICATIONS_KEY, label: 'Medicamentos recetados', fillMode: 'auto' },
];

export const TEMPLATE_LIST_STYLE_LABELS: Record<TemplateListStyle, string> = {
  cards: 'Tarjetas',
  numbered: 'Numerada',
  bullets: 'Viñetas',
};

/** One entry of a list variable. */
export interface TemplateListItem {
  title: string;
  detail?: string;
}

/** Separates an item's title from its detail when a list is written as text. */
export const TEMPLATE_LIST_DETAIL_SEPARATOR = ' — ';

/** List values travel as text: one item per line, optional " — detail". */
export function parseTemplateListText(text: string | undefined): TemplateListItem[] {
  if (!text) {
    return [];
  }
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const at = line.indexOf(TEMPLATE_LIST_DETAIL_SEPARATOR);
      if (at < 0) {
        return { title: line };
      }
      const title = line.slice(0, at).trim();
      const detail = line.slice(at + TEMPLATE_LIST_DETAIL_SEPARATOR.length).trim();
      return detail ? { title, detail } : { title };
    });
}

export function formatTemplateListItems(items: TemplateListItem[]): string {
  return items
    .filter((item) => item.title.trim())
    .map((item) =>
      item.detail?.trim()
        ? `${item.title.trim()}${TEMPLATE_LIST_DETAIL_SEPARATOR}${item.detail.trim()}`
        : item.title.trim()
    )
    .join('\n');
}

export const CUSTOM_FIELD_KEY_PREFIX = 'libre.campo_';
export const CUSTOM_LIST_KEY_PREFIX = 'libre.lista_';

/** Matches {{paciente.nombre}} style tokens in template text. */
export const PLACEHOLDER_TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

export function placeholderToken(key: string): string {
  return `{{${key}}}`;
}

/** Replace {{key}} tokens using values map; unknown keys stay as-is. */
export function resolvePlaceholderText(
  text: string,
  values: Record<string, string>
): string {
  return text.replace(PLACEHOLDER_TOKEN_PATTERN, (match, key: string) => {
    const value = values[key];
    return value != null && value !== '' ? value : match;
  });
}

export function extractPlaceholderKeys(text: string): string[] {
  const keys = new Set<string>();
  for (const match of text.matchAll(PLACEHOLDER_TOKEN_PATTERN)) {
    keys.add(match[1]);
  }
  return [...keys];
}

export function isManualPlaceholderKey(
  key: string,
  customFields: TemplateCustomField[] = []
): boolean {
  if (customFields.some((field) => field.key === key)) {
    return true;
  }
  return TEMPLATE_MANUAL_PLACEHOLDERS.some((item) => item.key === key);
}

export function createCustomFieldKey(
  label: string,
  type: TemplateFieldType = 'text'
): string {
  const slug = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40);
  const suffix = crypto.randomUUID().slice(0, 8);
  const prefix = type === 'list' ? CUSTOM_LIST_KEY_PREFIX : CUSTOM_FIELD_KEY_PREFIX;
  return `${prefix}${slug || (type === 'list' ? 'lista' : 'texto')}_${suffix}`;
}

export function defaultTextStyle(): TemplateTextStyle {
  return { fontSize: 'md', align: 'left', bold: false };
}

export function newBlockId(): string {
  return crypto.randomUUID();
}

export function createEmptyTemplateDraft(): Pick<
  DocumentTemplate,
  'name' | 'pageSize' | 'includeBusinessHeader' | 'blocks' | 'customFields'
> {
  return {
    name: 'Nueva plantilla',
    pageSize: 'letter',
    includeBusinessHeader: true,
    blocks: [
      {
        id: newBlockId(),
        type: 'text',
        content: '',
        style: defaultTextStyle(),
      },
    ],
    customFields: [],
  };
}
